<template>
  <div class="content">
    <h2 class="text-2xl font-bold mb-4">How to Use Spin the Names</h2>

    <!-- Quick Start -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Quick Start</h3>
      <ol class="list-decimal ml-6">
        <li>Add names or options in the input panel.</li>
        <li>Click the Spin button or press the spacebar to spin the wheel.</li>
        <li>When the wheel stops, the selected result is shown clearly.</li>
      </ol>
      <p class="mt-2 text-stone-700 dark:text-stone-300">Tip: Keep entries concise to ensure labels are readable.</p>
    </div>

    <!-- Managing Entries -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Managing Entries</h3>
      <ul class="list-disc ml-6">
        <li><strong>Add:</strong> Type a name/option and add it to the wheel.</li>
        <li><strong>Edit/Remove:</strong> Update or remove entries from the list as needed.</li>
        <li><strong>Duplicates:</strong> If you need weighted results, you can add the same name multiple times.</li>
      </ul>
    </div>

    <!-- Spinning & Results -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Spinning & Results</h3>
      <ul class="list-disc ml-6">
        <li><strong>Spin:</strong> Use the Spin button or press the spacebar.</li>
        <li><strong>No repeats (optional):</strong> Remove selected items from the list to avoid repeat winners.</li>
        <li><strong>Session use:</strong> Continue spinning until you’re done; you can re-add or reset the list any
          time.</li>
      </ul>
    </div>

    <!-- Customization -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Customization</h3>
      <ul class="list-disc ml-6">
        <li><strong>Appearance:</strong> Adjust colors, slice styles, and other visual settings for your wheel.</li>
        <li><strong>Sound:</strong> Enable/disable audio and adjust volume to fit your environment.</li>
        <li><strong>Presentation:</strong> Use full-screen mode for classrooms or events.</li>
      </ul>
      <p class="mt-2 text-stone-700 dark:text-stone-300">See the “Sound” and “Randomness” sections for more details on
        behavior and audio.</p>
    </div>

    <!-- Saving Your Wheels -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Saving Your Wheels</h3>
      <ul class="list-disc ml-6">
        <li><strong>No account needed</strong> for basic spinning.</li>
        <li><strong>Create a free account</strong> to save wheels, access them across devices, and reuse configurations.
        </li>
        <li><strong>Manage:</strong> Rename, update, or duplicate saved wheels as your needs change.</li>
      </ul>
      <p class="mt-2 text-stone-700 dark:text-stone-300">Create a free account to save wheels and access them across
        devices.</p>
    </div>

    <!-- Sharing & Embedding -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Sharing & Embedding</h3>
      <ul class="list-disc ml-6">
        <li><strong>Share:</strong> Generate a shareable link so others can view or use your wheel.</li>
        <li><strong>Embed:</strong> Add the wheel to your website using an iframe. See the “Embedding” section for
          examples and parameters.</li>
      </ul>
      <p class="mt-2 text-stone-700 dark:text-stone-300">For high-traffic or commercial usage, contact <a
          href="mailto:ash@spinthenames.com" class="text-blue-500 hover:underline">ash@spinthenames.com</a>.</p>
    </div>

    <!-- Mobile & Accessibility -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Mobile & Accessibility Tips</h3>
      <ul class="list-disc ml-6">
        <li><strong>Mobile:</strong> Use landscape orientation for more screen space.</li>
        <li><strong>Labels:</strong> Keep names short for best readability.</li>
        <li><strong>Contrast:</strong> Choose high-contrast colors to improve visibility for all users.</li>
      </ul>
    </div>

    <!-- Troubleshooting -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">Troubleshooting</h3>
      <ul class="list-disc ml-6">
        <li><strong>Wheel not spinning:</strong> Refresh the page and try again. Ensure your browser is up to date.</li>
        <li><strong>Audio not playing:</strong> Check your device volume and browser autoplay settings.</li>
        <li><strong>Performance:</strong> Reduce the number of entries or simplify styling on lower-powered devices.
        </li>
      </ul>
      <p class="mt-2 text-stone-700 dark:text-stone-300">If issues persist, contact <a
          href="mailto:ash@spinthenames.com" class="text-blue-500 hover:underline">ash@spinthenames.com</a> with details
        (browser, device, steps to reproduce).</p>
    </div>

    <p class="mt-8 text-sm"><a href="#top" class="text-blue-500 hover:underline">Back to top</a></p>
  </div>
</template>

<script setup>
import '@components/ui/faq/faqStyles.css';
</script>
